import type { FilterConfig } from '@/types/dashboard'
/**
 * Helper to extract cell value from row
 */
export function getCellValue(row: Record<string, unknown>, columnProp: string): string | number {
    const cellData = row[columnProp]
    
    if (typeof cellData === 'object' && cellData != null && 'value' in cellData) {
        if (typeof cellData.value === 'string' || typeof cellData.value === 'number') {
            return cellData.value
        }
        return ''
    }
    
    if (cellData == null) {
        return ''
    }
    
    if (typeof cellData === 'string' || typeof cellData === 'number') {
        return cellData
    }
    
    return String(cellData)
}

/**
 * Check if value matches numeric range [min, max]
 */
export function checkNumericRange(cellValue: string | number, filterValues: Array<string | number>): boolean {
    if (filterValues.length < 2) {
        return true
    }
    
    const numValue = Number(cellValue)
    if (isNaN(numValue)) {
        return false
    }
    
    const min = Number(filterValues[0])
    const max = Number(filterValues[1])
    
    if (isNaN(min) || isNaN(max)) {
        return true
    }
    
    return numValue >= min && numValue <= max
}

/**
 * Check if value contains text (case-insensitive)
 */
export function checkTextContains(cellValue: string | number, filterValues: Array<string | number>): boolean {
    if (!filterValues.length) {
        return true
    }
    
    const filterText = String(filterValues[0]).toLowerCase()
    if (!filterText) {
        return true
    }
    
    return String(cellValue).toLowerCase().includes(filterText)
}

/**
 * Check if value exactly matches any of the filter values
 */
export function checkExactMatch(cellValue: string | number, filterValues: Array<string | number>): boolean {
    const cellValueStr = String(cellValue).trim()
    return filterValues.some(v => String(v).trim() === cellValueStr)
}

/**
 * Main function to check if cell value matches filter based on filter config
 */
export function matchesFilter(
    cellValue: string | number,
    filterConfig: FilterConfig,
    filterValues: Array<string | number>
): boolean {
    const filterType = filterConfig.filterType ?? 'exact'

    // Custom match function (highest priority)
    if (filterType === 'custom' && filterConfig.matchFn) {
        return filterConfig.matchFn(cellValue, filterValues)
    }
    // Range filter
    if (filterType === 'range') {
        return checkNumericRange(cellValue, filterValues)
    }
    // Text filter (contains)
    if (filterType === 'text') {
        return checkTextContains(cellValue, filterValues)
    }
    // Default: exact match (checkbox)
    return checkExactMatch(cellValue, filterValues)
}
